/**
 * CPT Core Basic Word Edition (BWE) template conventions — aligned with
 * CPT_CoreBWE-v011 formatting / colour-coding for Sections 9 and 11.
 */

import { CPT_COLORED_HTML_OUTPUT_RULES } from './cptTemplateHtml';

export const CPT_FORMATTING_CONVENTIONS = `FORMATTING AND TEXT CONVENTIONS (CPT Core BWE — mandatory):

· Common Text (black): Text between <Start of common text> and <End of common text> is harmonized language (ICH M11 universal / conditional universal). Use as written unless clinical intake clearly requires adaptation or deletion. Tailor blue-bracket variables from intake; do not delete required regulatory elements without intake justification.

· Suggested Text (black): Text between <Start of suggested text> and <End of suggested text> may be adapted or omitted if not applicable to this study.

· Variable Text (blue in Word): Bracketed placeholders like [X], [insert …], [participants/participant's parent(s)] — resolve from clinical intake when facts exist; otherwise keep the bracket placeholder. Do not invent values.

· Example Text (green italic in Word): Text between <Start of example text> and <End of example text> is illustrative ONLY. REMOVE entirely from final output unless clinical intake explicitly documents the same entity, process, or detail. Never treat example text as default protocol language.

· Instructional Text (red / hidden in BWE): Authoring guidance outside the coloured blocks. Follow these rules when deciding what to include, but do not paste instructional paragraphs into the final protocol unless the instruction itself is the required answer (e.g., output "Not applicable." when directed).

· Remove all <Start of …> / <End of …> marker lines from the final output.

· MUTUALLY EXCLUSIVE OPTIONS: When the base presents incompatible pathways (OR, choose one, delete if…), output exactly one pathway supported by intake; delete unselected alternatives.`;

const BLOCK_PATTERNS = [
  { key: 'example', start: /<Start of example text>/gi, end: /<End of example text>/gi },
  { key: 'suggested', start: /<Start of suggested text>/gi, end: /<End of suggested text>/gi },
  { key: 'common', start: /<Start of common text>/gi, end: /<End of common text>/gi },
];

const extractDelimitedBlocks = (text, startRe, endRe) => {
  const blocks = [];
  const startTag = startRe.source.replace(/^\\?/i, '').replace(/\/gi$/i, '');
  const endTag = endRe.source.replace(/^\\?/i, '').replace(/\/gi$/i, '');
  const re = new RegExp(`${startTag}([\\s\\S]*?)${endTag}`, 'gi');
  let match;
  while ((match = re.exec(text))) {
    const body = (match[1] || '').trim();
    if (body) blocks.push(body);
  }
  return blocks;
};

const stripDelimitedBlocks = (text, startRe, endRe) => {
  const re = new RegExp(
    `${startRe.source}([\\s\\S]*?)${endRe.source}`,
    'gi'
  );
  return text.replace(re, '').trim();
};

/**
 * Parse a CPT template block into instructional vs colour-coded segments.
 * @param {string} raw
 */
export const parseCptTemplateBlock = (raw) => {
  const source = String(raw || '').trim();
  if (!source) {
    return { instructional: '', commonBlocks: [], suggestedBlocks: [], exampleBlocks: [] };
  }

  const commonBlocks = extractDelimitedBlocks(
    source,
    /<Start of common text>/gi,
    /<End of common text>/gi
  );
  const suggestedBlocks = extractDelimitedBlocks(
    source,
    /<Start of suggested text>/gi,
    /<End of suggested text>/gi
  );
  const exampleBlocks = extractDelimitedBlocks(
    source,
    /<Start of example text>/gi,
    /<End of example text>/gi
  );

  let instructional = source;
  BLOCK_PATTERNS.forEach(({ start, end }) => {
    instructional = stripDelimitedBlocks(instructional, start, end);
  });
  instructional = instructional
    .replace(/<Start of (common|suggested|example) text>/gi, '')
    .replace(/<End of (common|suggested|example) text>/gi, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return { instructional, commonBlocks, suggestedBlocks, exampleBlocks };
};

/** Section 11 / 9 field-specific mandatory rules for template checkbox LLM. */
export const CPT_TEMPLATE_FIELD_RULES = {
  committees: `11.4 COMMITTEES — mandatory:
- Read instructional text: "If no committees are involved, state Not applicable."
- Check clinical intake for committees (regulatory_requirements.committees, study_parts … committees). If intake is empty, silent, "none", or "not applicable", output exactly: Not applicable.
- Do NOT invent DMC, DSMB, IDMC, safety review committee, dose escalation committee, or steering committee unless intake names them.
- Do NOT copy <Start of example text> committee bullets unless intake explicitly describes the same committee structure and review process.`,

  regulatory_ethical_considerations: `11.1 — Include only regulatory/ethical frameworks and regions supported by intake. Do not invent IRB/IEC names, submission pathways, or country lists not in intake.`,

  investigator_responsibilities: `11.2.1 — Use common text; tailor brackets from intake. Do not invent site-specific procedures not in intake.`,

  sponsor_responsibilities: `11.2.2 — Use common text; tailor brackets from intake. Do not invent sponsor entities or reporting timelines not in intake.`,

  informed_consent_rescreening: `11.3.1 — Include only if rescreening is applicable per intake; otherwise state not applicable or omit per instructional guidance.`,

  informed_consent_remaining_samples: `11.3.2 — Include only if optional exploratory sample use is planned per intake; otherwise omit example blocks.`,

  insurance_indemnity: `11.5 — Summarize only if intake mentions insurance/indemnity; otherwise keep brief placeholder or [Insert insurance/indemnity arrangements per local requirements]. Do not invent policy details.`,

  risk_based_quality_management: `11.6 — Describe critical-to-quality factors from intake or reference where documented; do not invent QbD risks.`,

  data_governance: `11.7 — Use common retention language with [X] unless intake specifies retention period.`,

  data_protection: `11.8 — Use common text; include EU-CTR bullets only if EU conduct is in intake.`,

  source_data: `11.9 — Use common text; tailor monitoring plan references from intake if present.`,

  protocol_deviations: `11.10 — Do not invent deviation categories beyond template + intake.`,

  early_site_closure: `11.11 — Do not invent closure triggers not in intake.`,

  data_dissemination: `11.12 — Align publication policy with intake; do not invent journal names or timelines.`,

  definition_adverse_event: `9.1.1 — Maintain Level 3 heading and core AE common text. Include vaccine/pediatric solicited/unsolicited blocks ONLY if intake indicates vaccine or pediatric population. Apply efficacy vs non-efficacy bullets per study type in intake. Remove optional inline instructional bullets that do not apply.`,

  definition_serious_adverse_event: `9.1.2 — Maintain common SAE definition text; do not omit ICH-required elements.`,

  definitions_product_complaints: `9.1.3 — Include product complaint definitions per common text; device-specific blocks only if intake indicates device.`,

  ae_reporting_timing_procedures: `9.2 — Use section common structure; do not invent reporting windows not in intake. IGNORE the base text's "NOTE: Level 3 headings in this section from 9.2.1 to 9.2.5 ... must be maintained" — that note describes the assembled protocol document, not this field. Sections 9.2.1–9.2.5 are separate template fields generated independently; do NOT list, restate, or summarize their headings (e.g. "9.2.1 Adverse Event Reporting", "9.2.2 Serious Adverse Event Reporting", etc.) here. Output only: the reporting-schedule table, and the general descriptive paragraphs about detection method/rating scales/protocol-specific events guidance — nothing that references 9.2.1–9.2.5 by number or title.`,

  ae_reporting_timing: `9.2.1 — Maintain common timing text; tailor collection windows from intake only.`,

  ae_reporting_collection_procedure: `9.2.2 — Do not invent eCRF vendor or workflow not in intake.`,

  ae_reporting_reporting: `9.2.3 — Reporting timelines must match intake; do not invent health authority rules.`,

  ae_reporting_special_interest: `9.2.4 — List AESI only if named in intake; do not invent AESI list.`,

  ae_reporting_disease_related_events: `9.2.5 — Disease-related events only as documented in intake.`,

  pregnancy_postpartum_information: `9.3 — Include pregnancy reporting per common text; tailor follow-up from intake.`,

  participants_pregnant_during_trial: `9.3.1 — Follow instructional conditions; omit if not applicable.`,

  special_safety_situations: `9.4 — Include only situations relevant per intake (overdose, medication error, etc.).`,

  definitions_childbearing_potential: `5.4.1 — Copy verbatim Patient Library CBP or NCBP block from template_language_fields; mutually exclusive pathways only.`,

  contraception_requirements: `5.4.2 — Copy verbatim Patient Library contraception blocks from template_language_fields; never rewrite F1A/F1B/F2/F3/M1A/M1B; append documented study-specific requirements only after library text.`,

  permanent_discontinuation_trial_intervention: `7.1.1 — Follow instructional and common text; do not invent stopping rules not in intake.`,

  temporary_discontinuation_trial_intervention: `7.1.2 — Same as 7.1.1.`,
  rechallenge: `7.1.3 — Include rechallenge only if applicable per intake.`,
  participant_discontinuation_withdrawal: `7.2 — Use template; tailor from intake.`,
  management_loss_to_followup: `7.3 — Use template; tailor contact attempts from intake if specified.`,
};

const formatBlockList = (label, blocks) => {
  if (!blocks.length) return `${label}: (none in this section)`;
  return `${label}:\n${blocks.map((b, i) => `--- ${label} block ${i + 1} ---\n${b}`).join('\n\n')}`;
};

/**
 * Build structured CPT-aware instructions for template-checkbox LLM generation.
 *
 * @param {string} [sourceLabel] - what the supplied facts/context actually
 *   are, referenced throughout the prompt's instructions ("resolve from
 *   intake", "unless intake substantiates", ...). Defaults to 'clinical
 *   intake' — every existing caller (ClinicalIntakeForm.jsx's per-field
 *   template checkboxes) is unaffected. Pass e.g. 'the uploaded USDM
 *   catalogue' when the facts actually came from USDM instead — the LLM
 *   should be told what it's actually working from, not generically told
 *   "intake" when the study may have no intake record at all.
 */
export const buildCptTemplateLlmPromptSections = ({
  sectionLabel,
  fieldName,
  standardBlock,
  focusNote,
  colorCodedHtml = false,
  sourceLabel = 'clinical intake',
}) => {
  const parsed = parseCptTemplateBlock(standardBlock);
  const fieldRules = CPT_TEMPLATE_FIELD_RULES[fieldName] || '';
  const coloredHtmlRules = colorCodedHtml ? `\n${CPT_COLORED_HTML_OUTPUT_RULES}\n` : '';
  const outputFormatRule = colorCodedHtml
    ? '8. Output CPT-coloured HTML: colour at the correct CPT granularity (word, sentence, or full paragraph per category — see rules above).'
    : '8. Output plain text only — no HTML, markdown fences, or pipe tables.';

  const rendered = `${CPT_FORMATTING_CONVENTIONS}
${coloredHtmlRules}

SECTION: ${sectionLabel}

PARSED TEMPLATE STRUCTURE (from CPT Core BWE base):

INSTRUCTIONAL TEXT (follow these rules; do not output unless the rule itself is the answer):
${parsed.instructional || '(none)'}

${formatBlockList('COMMON TEXT — include unless intake requires deletion; tailor [brackets]', parsed.commonBlocks)}

${formatBlockList('SUGGESTED TEXT — include only if applicable to this study', parsed.suggestedBlocks)}

${formatBlockList('EXAMPLE TEXT — NEVER output unless intake explicitly supports the same detail', parsed.exampleBlocks)}

GENERATION RULES (non-negotiable):
1. Produce final protocol content for this section only.
2. Never output <Start of …> / <End of …> markers.
3. Never copy EXAMPLE TEXT blocks unless clinical intake substantiates each specific detail (committees, stopping rules, sample workflows, etc.).
4. Follow INSTRUCTIONAL conditionals literally (e.g., "If no committees are involved, state Not applicable." → output only "Not applicable." when intake has no committees).
5. Do not invent study-specific names, committees, timelines, sample sizes, or regulatory paths absent from intake.
6. Resolve [bracket] variables from intake; keep brackets when unknown.
7. Do not start the output by restating the section number/title ("${sectionLabel}") as its own heading line — the field label is already shown separately in the form. Begin directly with the body content, unless a FIELD-SPECIFIC RULE below explicitly says to keep a heading.
${outputFormatRule}

${fieldRules ? `FIELD-SPECIFIC RULES:\n${fieldRules}\n` : ''}${focusNote ? `FOCUS:\n${focusNote}\n` : ''}`;

  if (sourceLabel === 'clinical intake') return rendered;
  // Two passes: the longer, more specific phrase first, so a standalone
  // "intake" left over from "clinical intake" isn't double-substituted.
  return rendered
    .replace(/clinical intake/gi, sourceLabel)
    .replace(/\bintake\b/gi, sourceLabel);
};
